import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout (default)
});

// Long-running endpoints (SMTP email with PDF attachments, large report exports,
// backup imports/exports) can legitimately take 60-90s. Give them a much bigger
// window so we don't time out at 30s while the backend is still trying to
// deliver — that would show the user a bare "Failed to send email" toast even
// though the send may actually succeed a second later.
api.interceptors.request.use((config) => {
  const url = config.url || '';
  const isLongRunning = (
    /\/send-email\b/.test(url) ||
    /\/send-coa-email\b/.test(url) ||
    /\/send-pallet-label-email\b/.test(url) ||
    /\/recall\/.*\/send-emails/.test(url) ||
    /\/notify-fsai/.test(url) ||
    /\/backups\/(export|import|restore)/.test(url) ||
    /\/reports\/transport/.test(url)
  );
  if (isLongRunning) {
    config.timeout = 180000; // 3 min for SMTP w/ attachments and large exports
  }
  return config;
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and network issues
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Log network errors for debugging
    if (!error.response) {
      console.error('Network error:', error.message);
    }
    return Promise.reject(error);
  }
);

// ── SEC-001: Short-lived download tokens for URL-based exports ──
// Session JWTs must never appear in URL query strings (browser history / proxy
// logs replay risk). Instead, mint a purpose-scoped token via POST /download-tokens
// then embed that in the `?token=...` URL. Server enforces a 10-minute TTL and
// binds the token to one document; re-opening within the window is allowed so
// tab reloads / Save-in-viewer keep working.
export const mintDownloadToken = async (purpose, resourceId = null, ttlSeconds = 600) => {
  const { data } = await api.post('/download-tokens', {
    purpose,
    resource_id: resourceId || undefined,
    ttl_seconds: ttlSeconds,
  });
  return data.token;
};

/**
 * Open a backend URL in a new tab using a freshly-minted download token.
 * The token appears in the URL query string but is single-use and expires
 * quickly, so history/log capture is not exploitable.
 *
 * @param {string} path — API path starting with '/', e.g. `/dispatches/abc/pdf`
 * @param {object} params — optional query params to include
 * @param {string} purpose — audit label, e.g. 'dispatch_pdf'
 * @param {string} resourceId — optional resource id for audit context
 */
export const openSecureDownload = async (path, { params = {}, purpose = 'download', resourceId = null } = {}) => {
  const token = await mintDownloadToken(purpose, resourceId);
  const qp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) qp.append(k, v);
  });
  qp.set('token', token);
  const url = `${API_BASE}${path}?${qp.toString()}`;
  window.open(url, '_blank');
  return url;
};


// Auth
export const checkSetup = () => api.get('/auth/check-setup');
export const setupTenant = (tenantData, adminData) => 
  api.post('/auth/setup-tenant', { 
    name: tenantData.name,
    admin_name: adminData.name,
    email: adminData.email,
    password: adminData.password,
    phone: adminData.phone
  });
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me', { timeout: 12000 });
export const changePassword = (data) => api.post('/auth/change-password', data);
export const setupMFA = () => api.post('/auth/mfa/setup');
export const verifyMFA = (code) => api.post('/auth/mfa/verify', { code });
export const disableMFA = (code) => api.post('/auth/mfa/disable', { code });
export const trustDevice = () => api.post('/auth/trust-device');
export const requestMagicLink = (data) => api.post('/auth/request-magic-link', data);
export const verifyMagicLink = (token) => api.post(`/auth/verify-magic-link?token=${token}`);
export const refreshToken = () => api.post('/auth/refresh');
export const getSessionInfo = () => api.get('/auth/session-info');

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const resetUserPassword = (id) => api.post(`/users/${id}/reset-password`);
export const resetUserMFA = (id) => api.post(`/users/${id}/reset-mfa`);
export const sendUserCredentials = (id) => api.post(`/users/${id}/send-credentials`);

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const checkCustomerDependencies = (id) => api.get(`/customers/${id}/dependencies`);
export const deleteCustomerCascade = (id, data) => api.post(`/customers/${id}/delete-cascade`, data);
export const importCustomers = (file, strict = false) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/customers/import?strict=${strict}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getCustomerAddresses = (customerId) => api.get(`/customers/${customerId}/addresses`);
export const addCustomerAddress = (customerId, data) => api.post(`/customers/${customerId}/addresses`, data);
export const updateCustomerAddress = (customerId, addressId, data) => api.put(`/customers/${customerId}/addresses/${addressId}`, data);
export const deleteCustomerAddress = (customerId, addressId) => api.delete(`/customers/${customerId}/addresses/${addressId}`);
export const exportCustomers = () => `${API_BASE}/customers/export`;

// Reference Data Import (Products, Co-Branding, Customers from single file)
export const importReferenceData = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/reference-data', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Co-Branding
export const getCoBrandings = (params) => api.get('/cobranding', { params });
export const getCoBranding = (id) => api.get(`/cobranding/${id}`);
export const createCoBranding = (data) => api.post('/cobranding', data);
export const updateCoBranding = (id, data) => api.put(`/cobranding/${id}`, data);
export const deleteCoBranding = (id) => api.delete(`/cobranding/${id}`);
export const bulkDeleteCoBranding = (ids) => api.post('/cobranding/bulk-delete', { ids });

// Customer Product Mappings (Supplier Product Codes)
export const getCustomerProductMappings = (params) => api.get('/customer-product-mappings', { params });
export const getCustomerProductMapping = (id) => api.get(`/customer-product-mappings/${id}`);
export const createCustomerProductMapping = (data) => api.post('/customer-product-mappings', data);
export const updateCustomerProductMapping = (id, data) => api.put(`/customer-product-mappings/${id}`, data);
export const deactivateCustomerProductMapping = (id) => api.delete(`/customer-product-mappings/${id}`);
export const lookupSupplierProductCode = (customerId, productId) => 
  api.get('/customer-product-mappings/lookup', { params: { customer_id: customerId, product_id: productId } });
export const getCustomerMappingsLookup = (customerId) => 
  api.get(`/customer-product-mappings/by-customer/${customerId}`);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const checkProductDependencies = (id) => api.get(`/products/${id}/dependencies`);
export const deleteProductCascade = (id, data) => api.post(`/products/${id}/delete-cascade`, data);
export const bulkUpdateProductCategories = (productIds, categories, action = 'set') => 
  api.post('/products/bulk-categories', { product_ids: productIds, categories, action });
export const importProducts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/products/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Product calculation factors
export const learnProductFactors = (productId, data) => api.post(`/products/${productId}/learn-factors`, data);
export const calculateProductValues = (productId, params) => api.get(`/products/${productId}/calculate`, { params });

// Templates
export const getCustomerTemplate = () => `${API_BASE}/templates/customers`;
export const getProductTemplate = () => `${API_BASE}/templates/products`;

// Batches
export const getBatches = (params) => api.get('/batches', { params });
export const getBatch = (id) => api.get(`/batches/${id}`);
export const createBatch = (data) => api.post('/batches', data);
export const updateBatch = (id, data) => api.put(`/batches/${id}`, data);
export const deleteBatch = (id) => api.delete(`/batches/${id}`);
export const getBatchTraceability = (id) => api.get(`/batches/${id}/traceability`);
export const updateBatchQCStatus = (id, data) => api.post(`/batches/${id}/qc-status`, data);
export const getAvailableBatches = (params) => api.get('/batches/available', { params });

// Transformations (Product Splitting/Cutting)
export const getTransformations = (params) => api.get('/transformations', { params });
export const getTransformation = (id) => api.get(`/transformations/${id}`);
export const createTransformation = (data) => api.post('/transformations', data);

// Product children (for master/child relationship)
export const getChildProducts = (productId) => api.get(`/products/${productId}/children`);

// Dispatches
export const getDispatches = (params) => api.get('/dispatches', { params });
export const getNextDispatchNumber = () => api.get('/dispatches/next-number');
export const checkDuplicatePo = (customerId, poNumber, excludeOrderId, { signal } = {}) =>
  api.get('/dispatches/check-duplicate-po', {
    params: {
      customer_id: customerId,
      po_number: poNumber,
      ...(excludeOrderId ? { exclude_order_id: excludeOrderId } : {}),
    },
    signal,
  });
export const getDispatch = (id) => api.get(`/dispatches/${id}`);
export const createDispatch = (data) => api.post('/dispatches', data);
export const updateDispatch = (id, data) => api.put(`/dispatches/${id}`, data);
export const lockDispatch = (id) => api.post(`/dispatches/${id}/lock`);
export const unlockDispatch = (id, reason) => api.post(`/dispatches/${id}/unlock`, null, { params: { reason } });
export const markDispatched = (id, upliftDate) =>
  api.post(`/dispatches/${id}/dispatch${upliftDate ? `?uplift_date=${encodeURIComponent(upliftDate)}` : ''}`);
export const updateDispatchStatus = (id, status, batchLines, dispatchLines, upliftDate) =>
  api.post(`/dispatches/${id}/status`, {
    status,
    batch_lines: batchLines || undefined,
    dispatch_lines: dispatchLines || undefined,
    uplift_date: upliftDate || undefined,
  });
export const softDeleteDispatch = (id, data) => api.post(`/dispatches/${id}/delete`, data);
export const getDeletedDispatches = (params) => api.get('/dispatches/deleted', { params });
export const restoreDispatch = (id) => api.post(`/dispatches/${id}/restore`);

// ─── Phased dispatch (Split Fulfilment) ───
export const splitFulfilment = (id, data) => api.post(`/dispatches/${id}/split-fulfilment`, data);
export const mergeBalance = (id) => api.post(`/dispatches/${id}/merge-balance`);
export const getPoGroup = (groupId) => api.get(`/po-groups/${groupId}`);

// ─── Stock Returns & Dispatch Reversal (core) ───
export const createReturn = (data) => api.post('/returns', data);
export const getReturns = (params) => api.get('/returns', { params });
export const getReturn = (id) => api.get(`/returns/${id}`);
export const getDispatchReturns = (dispatchId) => api.get(`/dispatches/${dispatchId}/returns`);
export const reverseDispatch = (id, data) => api.post(`/dispatches/${id}/reverse`, data);
export const undoReverseDispatch = (id, data) => api.post(`/dispatches/${id}/undo-reverse`, data);
export const voidReturn = (id, data) => api.post(`/returns/${id}/void`, data);
export const linkReturnRedistribution = (id, dispatchId) =>
  api.post(`/returns/${id}/redistribution`, { dispatch_id: dispatchId });
// SEC-001: opens the return-note PDF in a new tab via a single-use download token.
export const openReturnPdf = async (id) => {
  await openSecureDownload(`/returns/${id}/pdf`, { purpose: 'return_pdf', resourceId: id });
};
export const getDispatchEditHistory = (id) => api.get(`/dispatches/${id}/edit-history`);
export const getDispatchEmailTemplate = (id) => api.get(`/dispatches/${id}/email-template`);
export const sendDispatchEmail = (id, data) => api.post(`/dispatches/${id}/send-email`, data);
export const sendCOAEmail = (id, data) => api.post(`/dispatches/${id}/send-coa-email`, data);
export const sendPalletLabelEmail = (id, data) => api.post(`/dispatches/${id}/send-pallet-label-email`, data);
export const getCustomerEmailRecipients = (customerId, docType = 'dispatch_note') =>
  api.get(`/customers/${customerId}/email-recipients`, { params: { doc_type: docType } });

// AccountsIQ
export const getAccountsIQSyncErrors = (params = {}) => api.get('/accountsiq/sync-errors', { params });
export const retryAccountsIQError = (id) => api.post('/accountsiq/sync-errors/retry', { id });
export const resolveAccountsIQError = (id) => api.post('/accountsiq/sync-errors/resolve', { id });

// Invoice status (manual or auto-set by AIQ)
export const toggleInvoiced = (dispatchId, invoiced, note = '') =>
  api.put(`/dispatches/${dispatchId}/invoiced`, { invoiced, note });
export const bulkInvoiced = (dispatchIds, invoiced, note = '') =>
  api.post('/dispatches/bulk-invoiced', { dispatch_ids: dispatchIds, invoiced, note });
// SEC-001: opens the finalized dispatch PDF via a single-use download token.
export const openDispatchPdf = async (id) => {
  await openSecureDownload(`/dispatches/${id}/download-pdf`, { purpose: 'dispatch_pdf', resourceId: id });
};
// SEC-001: opens the in-progress dispatch PDF via a single-use download token.
export const openDispatchPdfInline = async (id) => {
  await openSecureDownload(`/dispatches/${id}/pdf`, { purpose: 'dispatch_pdf', resourceId: id });
};
// SEC-001: opens the Certificate of Analysis PDF via a single-use download token.
export const openCoaPdf = async (id) => {
  await openSecureDownload(`/dispatches/${id}/coa-pdf`, { purpose: 'coa_pdf', resourceId: id });
};
// SEC-001: opens customer XLSX export via a single-use download token.
export const openCustomersExport = async () => {
  await openSecureDownload('/customers/export', { purpose: 'customers_export' });
};
export const printLabel = (dispatchId, lineId) => 
  api.post(`/dispatches/${dispatchId}/lines/${lineId}/print-label`);

// Order Pipeline - Status Management
export const checkOrderStatusChange = (orderId, targetStatus) => 
  api.get(`/orders/${orderId}/status-check`, { params: { target_status: targetStatus } });
export const revertOrderStatus = (orderId, data) => 
  api.post(`/orders/${orderId}/revert-status`, data);

// Batches - soft delete
export const softDeleteBatch = (id, data) => api.post(`/batches/${id}/delete`, data);
export const getDeletedBatches = (params) => api.get('/batches/deleted', { params });
export const restoreBatch = (id) => api.post(`/batches/${id}/restore`);
export const bulkDeleteBatches = (batchIds) => api.post('/batches/bulk-delete', { batch_ids: batchIds });
export const hardDeleteBatch = (id) => api.delete(`/batches/${id}`);

// Recall
export const searchRecall = (data) => api.post('/recall/search', data);
// SEC-001: opens the recall export CSV/PDF in a new tab via a single-use download token.
export const openRecallExport = async (params, { format = 'pdf' } = {}) => {
  await openSecureDownload('/recall/export', {
    params: { ...params, format },
    purpose: `recall_export_${format}`,
  });
};

// Reports
export const getWeeklyOrdersReport = (params) => api.get('/reports/weekly-orders', { params });
export const getDispatchSummaryReport = (params) => api.get('/reports/dispatch-summary', { params });
export const getBatchRegisterReport = () => api.get('/reports/batch-register');
export const getAuditLogReport = (params) => api.get('/reports/audit-log', { params });
export const getAuditLogFilters = () => api.get('/reports/audit-log/filters');
export const getAuditLogStats = () => api.get('/reports/audit-log/stats');
export const cleanupAuditLogs = () => api.post('/reports/audit-log/cleanup');
export const getLabelPrintsReport = (params) => api.get('/reports/label-prints', { params });
export const getTraceabilityReport = (params) => api.get('/reports/traceability', { params, timeout: 120000 });
// Batch-level traceability PDF (figures per batch code, not order totals).
export const openTraceabilityPdf = async (params) => {
  await openSecureDownload('/reports/traceability/pdf', {
    params,
    purpose: 'traceability_batch_pdf',
    ttlSeconds: 300,
  });
};
export const getQCStatusReport = (params) => api.get('/reports/qc-status', { params });
export const bulkApproveQC = (data) => api.post('/reports/qc-status/bulk-approve', data);
export const getOOSOrdersReport = (params) => api.get('/reports/oos-orders', { params });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getProductFormats = () => api.get('/settings/product-formats');
export const getProductCategories = () => api.get('/settings/product-categories');
export const getTransportOptions = () => api.get('/settings/transport-options');
export const testSMTP = (email) => api.post('/settings/smtp-test', { test_email: email });

// Permissions
export const getPermissionsList = () => api.get('/permissions/list');
export const updateUserPermissions = (userId, permissions) => api.put(`/users/${userId}/permissions`, permissions);

// Customer Product Mappings - Reverse lookup
export const reverseLookupSupplierCode = (customerId, supplierCode) => 
  api.get(`/customer-product-mappings/reverse-lookup?customer_id=${customerId}&supplier_code=${encodeURIComponent(supplierCode)}`);

// Backups
// Custom Roles
export const getCustomRoles = () => api.get('/roles/custom');
export const createCustomRole = (data) => api.post('/roles/custom', data);
export const updateCustomRole = (id, data) => api.put(`/roles/custom/${id}`, data);
export const deleteCustomRole = (id) => api.delete(`/roles/custom/${id}`);

export const getBackups = (params) => api.get('/backups', { params });
export const createBackup = (data) => api.post('/backups', data || {});
export const getBackup = (id) => api.get(`/backups/${id}`);
export const previewRestore = (id) => api.get(`/backups/${id}/preview`);
export const uploadPreview = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/backups/upload-preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const restoreBackup = (id, data) => api.post(`/backups/${id}/restore`, data);
export const deleteBackup = (id) => api.delete(`/backups/${id}`);
export const bulkDeleteBackups = (ids) => api.post('/backups/bulk-delete', { ids });
export const downloadBackup = (id) => api.get(`/backups/${id}/download`, { responseType: 'blob', timeout: 120000 });
// Streaming export — works for databases of any size (no timeout, blob response)
export const exportBackupStream = () => api.post('/backups/export-stream', {}, { responseType: 'blob', timeout: 0 });
export const exportBackupFile = () => api.post('/backups/export-file', {}, { responseType: 'blob', timeout: 120000 });
export const restoreFromFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/backups/restore-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
// Streaming restore — works for files of any size
export const restoreFromStream = (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/backups/restore-stream', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0,
    onUploadProgress,
  });
};
export const previewUploadStream = (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/backups/upload-stream-preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0,
    onUploadProgress,
  });
};
export const getBackupSettings = () => api.get('/backup-settings');
export const updateBackupSettings = (data) => api.put('/backup-settings', data);
// Manually trigger the daily auto-backup job right now — useful for
// verifying the schedule wiring and recovering from missed windows.
export const runAutoBackupNow = () => api.post('/backup-settings/run-now');
export const getBackupHealth = () => api.get('/backup-health');

// FTP Backup
export const getFTPSettings = () => api.get('/ftp-backup/settings');
export const saveFTPSettings = (data) => api.post('/ftp-backup/settings', data);
export const testFTPConnection = (data) => api.post('/ftp-backup/test-connection', data);
export const createMongodumpBackup = () => api.post('/ftp-backup/create');
export const getLocalBackups = () => api.get('/ftp-backup/local');
export const getRemoteBackups = () => api.get('/ftp-backup/remote');
export const restoreFTPBackup = (id) => api.post(`/ftp-backup/restore/${id}`);
export const restoreRemoteBackup = (path) => api.post('/ftp-backup/restore-remote', { path });
export const deleteFTPBackup = (id) => api.delete(`/ftp-backup/${id}`);
export const getFTPSchedule = () => api.get('/ftp-backup/schedule');
export const updateFTPSchedule = (data) => api.put('/ftp-backup/schedule', data);

// Order Lock Status
export const getOrderLockStatus = (dispatchId) => api.get(`/dispatches/${dispatchId}/lock-status`);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Reports
export const getSalesByCategoryReport = (params) => api.get('/reports/sales-by-category', { params });
// SEC-001: opens the sales-by-category PDF via a single-use download token.
export const openSalesByCategoryPdf = async (params) => {
  await openSecureDownload('/reports/sales-by-category/pdf', {
    params,
    purpose: 'sales_by_category_pdf',
  });
};

// Recall Reporting
export const getRecallByBatch = (batchCode, productId) => api.get('/reports/recall/by-batch', {
  params: productId
    ? { batch_code: batchCode, product_id: productId }
    : { batch_code: batchCode }
});
export const getRecallByUseByDate = (useByFrom, useByTo) => api.get('/reports/recall/by-use-by-date', { 
  params: { use_by_from: useByFrom, use_by_to: useByTo || useByFrom } 
});
// SEC-001: single-use download-token variants for the recall PDF endpoints.
export const openRecallByBatchPdf = async (batchCode, productId = null) => {
  const params = { batch_code: batchCode };
  if (productId) params.product_id = productId;
  await openSecureDownload('/reports/recall/by-batch/pdf', {
    params,
    purpose: 'recall_by_batch_pdf',
    resourceId: batchCode,
  });
};
export const openRecallByUseByDatePdf = async (useByFrom, useByTo = null) => {
  const params = { use_by_from: useByFrom };
  if (useByTo) params.use_by_to = useByTo;
  await openSecureDownload('/reports/recall/by-use-by-date/pdf', {
    params,
    purpose: 'recall_by_use_by_date_pdf',
  });
};

// Send recall email notification
export const sendRecallEmail = (data) => api.post('/reports/recall/send-email', data);

// Pallet Labels
// SEC-001: opens pallet labels via a single-use download token.
export const openPalletLabels = async (dispatchId, copies = 2, extra = {}) => {
  await openSecureDownload(`/dispatches/${dispatchId}/pallet-labels`, {
    params: { copies, ...extra },
    purpose: 'pallet_labels',
    resourceId: dispatchId,
  });
};

// Transport Report
// SEC-001: opens the transport report PDF via a single-use download token.
export const openTransportReportPdf = async (params) => {
  await openSecureDownload('/reports/transport', {
    params,
    purpose: 'transport_report',
  });
};
export const getTransportReport = (params) => api.get('/reports/transport', { params, responseType: 'blob', timeout: 60000 });

// Admin utilities
export const backfillSupplierCodes = () => api.post('/admin/backfill-supplier-codes');
export const purgeInactiveMappings = () => api.post('/customer-product-mappings/purge-inactive');
export const getInactiveMappingCount = () => api.get('/customer-product-mappings/inactive-count');

// Conversion formula
export const lookupFormula = (customerId, productId, cobrandingId) =>
  api.get('/customer-product-mappings/formula/lookup', { params: { customer_id: customerId, product_id: productId, cobranding_id: cobrandingId || undefined } });
export const updateFormula = (mappingId, data) =>
  api.put(`/customer-product-mappings/${mappingId}/formula`, data);
export const learnFormula = (data) =>
  api.post('/customer-product-mappings/formula/learn', data);
export const scanOrderFormulas = () =>
  api.post('/admin/scan-order-formulas');
export const scanBatchFormulas = () =>
  api.post('/admin/scan-batch-formulas');
export const updateBatchFormula = (mappingId, data) =>
  api.put(`/customer-product-mappings/${mappingId}/batch-formula`, data);
export const learnBatchFormula = (data) =>
  api.post('/customer-product-mappings/batch-formula/learn', data);

// Use-By date prediction (per customer product code)
export const predictUseByDates = (data) => api.post('/use-by/predict', data);
export const getUseByProfiles = (customerId) =>
  api.get('/use-by/profiles', { params: { customer_id: customerId } });
export const updateUseByProfile = (profileId, data) =>
  api.put(`/use-by/profiles/${profileId}`, data);
export const relearnUseByProfile = (profileId) =>
  api.post(`/use-by/profiles/${profileId}/relearn`);
export const scanUseByProfiles = () => api.post('/use-by/scan');

// Recall Management
export const createRecall = (data) => api.post('/recalls', data);
export const listRecalls = (params) => api.get('/recalls', { params });
export const getRecall = (id) => api.get(`/recalls/${id}`);
export const updateRecallStatus = (id, data) => api.put(`/recalls/${id}/status`, data);
export const addRecallComment = (id, data) => api.post(`/recalls/${id}/comments`, data);
export const getRecallRecipients = (id) => api.get(`/recalls/${id}/recipients`);
export const sendRecallEmails = (id, data) => api.post(`/recalls/${id}/send-emails`, data);
export const previewRecallEmail = (id, data) => api.post(`/recalls/${id}/preview-email`, data);
export const getRecallReplyTo = () => api.get(`/recalls/settings/reply-to`);
export const addRecallResponse = (id, data) => api.post(`/recalls/${id}/responses`, data);
export const deleteRecallResponse = (id, entryId) => api.delete(`/recalls/${id}/responses/${entryId}`);
// SEC-001: single-use download-token variants for recall management PDFs and per-customer ZIPs.
export const openRecallPdf = async (id, { customerSide = false, customerName = null } = {}) => {
  const params = {};
  if (customerSide) params.customer_side = 'true';
  if (customerName) params.customer_name = customerName;
  await openSecureDownload(`/recalls/${id}/pdf`, {
    params,
    purpose: customerSide ? 'recall_customer_pdf' : 'recall_pdf',
    resourceId: id,
  });
};
export const openRecallPerCustomerZip = async (id) => {
  await openSecureDownload(`/recalls/${id}/per-customer-pdfs.zip`, {
    purpose: 'recall_per_customer_zip',
    resourceId: id,
  });
};
// SEC-001: opens the full comprehensive recall summary PDF (audit-ready, all
// events/logs/dispatches/customers) via a single-use download token.
export const openRecallSummaryPdf = async (id) => {
  await openSecureDownload(`/recalls/${id}/summary-pdf`, {
    purpose: 'recall_full_summary_pdf',
    resourceId: id,
    ttlSeconds: 300,
  });
};

// Role-name migration
export const getRoleMigrationStatus = () => api.get('/users/role-migration/status');
export const runRoleMigration = () => api.post('/users/role-migration/run');

// Template field-refresh (adds new default fields to existing templates)
export const refreshTemplateFields = () => api.post('/templates/refresh-fields');

// Dashboard live widgets
export const getExpiringStock = (days = 14) => api.get('/dashboard/expiring-stock', { params: { days } });
export const getRecentActivity = (limit = 8) => api.get('/dashboard/recent-activity', { params: { limit } });
export const getSystemAlerts = () => api.get('/dashboard/alerts');
export const getWeeklyPlan = (params) => api.get('/dashboard/weekly-plan', { params });
export const getWeeklyPlanPrefs = () => api.get('/dashboard/weekly-plan/preferences');
export const saveWeeklyPlanPrefs = (prefs) => api.put('/dashboard/weekly-plan/preferences', prefs);

// Account self-service
export const updateProfile = (data) => api.put('/account/profile', data);

// Operators
export const getOperators = () => api.get('/operators');
export const createOperator = (data) => api.post('/operators', data);
export const updateOperator = (id, data) => api.put(`/operators/${id}`, data);
export const deleteOperator = (id) => api.delete(`/operators/${id}`);
export const verifyOperatorCode = (code) => api.post('/operators/verify', { code });

// API Keys
export const getApiKeys = () => api.get('/api-keys');
export const createApiKey = (data) => api.post('/api-keys', data);
export const revokeApiKey = (id) => api.delete(`/api-keys/${id}`);

// Compliance
export const getComplianceLogs = (params) => api.get('/compliance/logs', { params });
export const getComplianceSummary = () => api.get('/compliance/summary');
export const getComplianceActions = () => api.get('/compliance/actions');
export const generateComplianceReport = (params) => api.post('/compliance/generate-report', null, { params });
export const listComplianceReports = (params) => api.get('/compliance/reports', { params });
export const downloadComplianceReport = (id) => api.get(`/compliance/download-report/${id}`, { responseType: 'blob' });
export const verifyComplianceChain = () => api.get('/compliance/verify-chain');

// Security Audit Dashboard
export const getSecuritySummary = (range) => api.get('/security-audit/summary', { params: { time_range: range } });
export const getLoginHeatmap = (range) => api.get('/security-audit/login-heatmap', { params: { time_range: range } });
export const getSecurityEvents = (range, type, page) => api.get('/security-audit/events', { params: { time_range: range, event_type: type, page } });
export const getActiveLockouts = () => api.get('/security-audit/lockouts');
export const unlockAccount = (email) => api.post('/security-audit/unlock', null, { params: { email } });
export const getSecurityAnomalies = (range) => api.get('/security-audit/anomalies', { params: { time_range: range } });
export const getTopIPs = (range) => api.get('/security-audit/top-ips', { params: { time_range: range } });

// Order Purge
export const searchOrdersForPurge = (q) => api.get('/order-purge/search', { params: { q } });
export const purgeOrder = (dispatch_id, reason) => api.post('/order-purge/purge', { dispatch_id, reason });
export const getDeletionHistory = () => api.get('/order-purge/history');

// Dispatch Note Purge (standalone notes raised in error)
export const searchDispatchNotesForPurge = (q, includeLinked = false) =>
  api.get('/dispatch-purge/search', { params: { q, include_linked: includeLinked } });
export const getDispatchNotePurgeEligibility = (id) => api.get(`/dispatch-purge/${id}/eligibility`);
export const purgeDispatchNote = (dispatch_id, reason, confirm_dispatch_number) =>
  api.post('/dispatch-purge/purge', { dispatch_id, reason, confirm_dispatch_number });
export const getDispatchNotePurgeHistory = () => api.get('/dispatch-purge/history');

// ── Suppliers (FSAI one-step-back) ──
export const listSuppliers = (params) => api.get('/suppliers', { params });
export const getSupplier = (id) => api.get(`/suppliers/${id}`);
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);
export const getSupplierTypes = () => api.get('/suppliers/types');

// ── AccountsIQ invoice tracking ──
export const markDispatchInvoiced = (dispatchId, data) => api.post(`/dispatches/${dispatchId}/mark-invoiced`, data);
export const clearDispatchInvoice = (dispatchId) => api.post(`/dispatches/${dispatchId}/clear-invoice`);

// ── AccountsIQ sync + reviewed draft invoices ──
export const getAiqSyncStatus = () => api.get('/accountsiq/sync/status');
export const refreshAiqSync = () => api.post('/accountsiq/sync/refresh');
export const getAiqReference = () => api.get('/accountsiq/reference');
export const getAiqCustomerReview = () => api.get('/accountsiq/customers/review');
export const importAiqCustomers = (codes) => api.post('/accountsiq/customers/import', { codes });
export const applyAiqCustomer = (customerId, fields) => api.post(`/accountsiq/customers/${customerId}/apply`, { fields });
export const linkAiqCustomer = (customerId, code) => api.post(`/accountsiq/customers/${customerId}/link`, { code });
export const ignoreAiqCustomer = (code) => api.post('/accountsiq/customers/ignore', { code });
export const getAiqProductReview = () => api.get('/accountsiq/products/review');
export const resolveAiqProduct = (payload) => api.post('/accountsiq/products/resolve', payload);
export const setProductInvoiceBasis = (productId, basis) => api.put(`/accountsiq/products/${productId}/invoice-basis`, { basis });
export const getAiqInvoicePreview = (dispatchId) => api.get(`/dispatches/${dispatchId}/accountsiq/preview`);
export const sendAiqInvoice = (dispatchId, previewHash) => api.post(`/dispatches/${dispatchId}/accountsiq/send`, { preview_hash: previewHash });
export const runAiqHealthCheck = () => api.post('/accountsiq/health-check');
export const createAiqTestInvoice = (payload) => api.post('/accountsiq/health-check/test-invoice', payload);

// ── Recall hardening (FSAI / BRC) ──
export const getRecallStandaloneBatches = (recallId) => api.get(`/recalls/${recallId}/standalone-batches`);
export const getRecallScorecard = (recallId) => api.get(`/recalls/${recallId}/scorecard`);
export const notifyFsaiForRecall = (recallId, payload) => api.post(`/recalls/${recallId}/notify-fsai`, payload);
export const listLinesMissingBatchCode = (params) => api.get('/recalls/admin/lines-missing-batch-code', { params });
export const patchLineBatchCode = (data) => api.post('/recalls/admin/patch-line-batch-code', data);

// Batch corrections (admin tool — restricted by permission can_correct_batch_data)
export const batchCorrectionLookupDispatch = (dispatchNumber) =>
  api.get(`/batch-corrections/dispatch/${encodeURIComponent(dispatchNumber)}`);
export const batchCorrectionLookupBatch = (batchCode) =>
  api.get('/batch-corrections/batch', { params: { batch_code: batchCode } });
export const batchCorrectionImpactPreview = (dispatch_id, line_index) =>
  api.get('/batch-corrections/impact', { params: { dispatch_id, line_index } });
export const batchCorrectionApply = (corrections, reason, propagate = true) =>
  api.post('/batch-corrections/apply', { corrections, reason, propagate });
export const batchCorrectionHistory = (limit = 50) =>
  api.get('/batch-corrections/history', { params: { limit } });
export const batchCorrectionZipPdf = (ids) =>
  api.get('/batch-corrections/dispatches/zip-pdf', {
    params: { ids: ids.join(',') },
    responseType: 'blob',
  });
export const batchCorrectionBulkEmail = (dispatch_ids, extra_message = '') =>
  api.post('/batch-corrections/dispatches/bulk-email', { dispatch_ids, extra_message });

// Data management — feature timeline + safe data-tools
export const getFeatureTimeline = (category) =>
  api.get('/data-management/feature-timeline', { params: category ? { category } : {} });
export const aliasBackfillDryRun = (customer_id = null) =>
  api.post('/data-management/backfill/alias-on-dispatches', { customer_id, dry_run: true });
export const aliasBackfillApply = (customer_id = null, reason = '') =>
  api.post('/data-management/backfill/alias-on-dispatches', { customer_id, dry_run: false, reason });

export default api;
